import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAdminHelpCenterArticles,
  createHelpArticle,
  updateHelpArticle,
  publishHelpArticle,
  unpublishHelpArticle,
} from "../../apis/supportService";
import {
  RefreshCw,
  ExternalLink,
  Plus,
  Eye,
  EyeOff,
  Save,
  CheckCircle,
  CircleSlash,
  Heading2,
  Heading3,
  Heading1,
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Code,
  Table,
  Image as ImageIcon,
  FilePlus,
} from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import MarkdownRenderer from "../help/MarkdownRenderer";

const emptyForm = {
  slug: "",
  title: "",
  summary: "",
  category: "",
  tags: "",
  bodyMarkdown: "",
};

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const mapArticleToForm = (article) => ({
  slug: article?.slug || "",
  title: article?.title || "",
  summary: article?.summary || "",
  category: article?.category || "",
  tags: Array.isArray(article?.tags) ? article.tags.join(", ") : article?.tags || "",
  bodyMarkdown: article?.bodyMarkdown || "",
});

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const HelpCenterAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0, limit: 50 });
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isPreview, setIsPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedArticleId = selectedArticle?.articleId ?? null;
  const contentRef = useRef(null);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        page,
        sortBy: "updatedAt",
        sortOrder: "DESC",
      };
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await fetchAdminHelpCenterArticles(params);
      const items = response?.items || [];
      setArticles(items);
      if (response?.pagination) setPageInfo(response.pagination);

      if (selectedArticleId) {
        const refreshed = items.find((item) => item.articleId === selectedArticleId);
        if (refreshed) {
          setSelectedArticle(refreshed);
          setForm(mapArticleToForm(refreshed));
        }
      }
    } catch (error) {
      console.error("Failed to load help articles", error);
      toast.error("Unable to load help center articles");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedArticleId, page, limit]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleSelectArticle = (article) => {
    setSelectedArticle(article);
    setForm(mapArticleToForm(article));
    setIsPreview(false);
  };

  const handleStartNew = () => {
    setSelectedArticle(null);
    setForm(emptyForm);
    setIsPreview(false);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "title" && !selectedArticleId && !prev.slug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const buildPayload = () => ({
    slug: form.slug.trim(),
    title: form.title.trim(),
    summary: form.summary.trim() || null,
    category: form.category.trim() || null,
    tags: form.tags,
    bodyMarkdown: form.bodyMarkdown,
  });

  const insertAtCursor = (text) => {
    const el = contentRef.current;
    if (!el) {
      handleFormChange("bodyMarkdown", (form.bodyMarkdown || "") + text);
      return;
    }
    const start = el.selectionStart ?? (form.bodyMarkdown?.length || 0);
    const end = el.selectionEnd ?? start;
    const current = form.bodyMarkdown || "";
    const next = current.slice(0, start) + text + current.slice(end);
    handleFormChange("bodyMarkdown", next);
    // restore cursor near inserted text
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
      } catch (_) {}
    });
  };

  const replaceSelection = (transform, fallback = "") => {
    const el = contentRef.current;
    const value = form.bodyMarkdown || "";
    if (!el) {
      const next = transform(value, 0, 0) || (value + fallback);
      handleFormChange("bodyMarkdown", next);
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || fallback;
    const after = value.slice(end);
    const changed = transform({ before, selected, after, start, end });
    if (!changed) return;
    handleFormChange("bodyMarkdown", changed.text);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const s = changed.selStart ?? start;
        const e = changed.selEnd ?? s;
        el.setSelectionRange(s, e);
      } catch (_) {}
    });
  };

  const wrapSelection = (pre, post, placeholder = "text") => {
    replaceSelection(({ before, selected, after, start }) => {
      const content = before + pre + selected + post + after;
      const selStart = start + pre.length;
      const selEnd = selStart + (selected ? selected.length : placeholder.length);
      return { text: content, selStart, selEnd };
    }, placeholder);
  };

  const prefixLines = (prefix) => {
    replaceSelection(({ before, selected, after, start }) => {
      const block = selected || "text";
      const lines = block.split(/\r?\n/);
      const changed = lines.map((l) => (l.startsWith(prefix) ? l : `${prefix}${l}`)).join("\n");
      const text = before + changed + after;
      const selStart = start;
      const selEnd = start + changed.length;
      return { text, selStart, selEnd };
    });
  };

  const quickInsert = {
    h1: () => prefixLines("# "),
    h2: () => prefixLines("## "),
    h3: () => prefixLines("### "),
    p: () => insertAtCursor("\n\nWrite a paragraph here...\n\n"),
    bold: () => wrapSelection("**", "**", "bold"),
    italic: () => wrapSelection("*", "*", "italic"),
    link: () => wrapSelection("[", "](https://example.com)", "link text"),
    ul: () => prefixLines("- "),
    ol: () => {
      replaceSelection(({ before, selected, after, start }) => {
        const block = selected || "First\nSecond\nThird";
        const lines = block.split(/\r?\n/);
        const changed = lines.map((l, i) => `${i + 1}. ${l.replace(/^\d+\.\s+/, "")}`).join("\n");
        const text = before + changed + after;
        return { text, selStart: start, selEnd: start + changed.length };
      });
    },
    quote: () => prefixLines("> "),
    code: () => wrapSelection("\n\n```\n", "\n```\n\n", "code block"),
    image: () => insertAtCursor("\n\n![alt text](https://example.com/image.png)\n\n"),
    table: () => insertAtCursor("\n\n| Column A | Column B |\n|---|---|\n| A1 | B1 |\n| A2 | B2 |\n\n"),
    template: () => insertAtCursor(
      "# Title (optional)\n\n" +
        "## Overview\nWrite a short summary.\n\n" +
        "## Steps\n1. First step\n2. Second step\n\n" +
        "## Tips\n> Add helpful tips here.\n\n"
    ),
  };

  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!payload.slug || !payload.title || !payload.bodyMarkdown) {
      toast.error("Slug, title, and content are required");
      return;
    }

    setSaving(true);
    try {
      let saved;
      if (selectedArticleId) {
        saved = await updateHelpArticle(selectedArticleId, payload);
        toast.success("Article updated");
      } else {
        saved = await createHelpArticle(payload);
        toast.success("Draft created");
      }
      setSelectedArticle(saved);
      setForm(mapArticleToForm(saved));
      await loadArticles();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedArticleId) return;
    setPublishing(true);
    try {
      const updated = await publishHelpArticle(selectedArticleId);
      toast.success("Article published");
      setSelectedArticle(updated);
      setForm(mapArticleToForm(updated));
      await loadArticles();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!selectedArticleId) return;
    setPublishing(true);
    try {
      const updated = await unpublishHelpArticle(selectedArticleId);
      toast.success("Article unpublished");
      setSelectedArticle(updated);
      setForm(mapArticleToForm(updated));
      await loadArticles();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to unpublish");
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedArticleId) return;
    const ok = window.confirm("Delete this article permanently? This cannot be undone.");
    if (!ok) return;
    setDeleting(true);
    try {
      const { deleteHelpArticle } = await import("../../apis/supportService");
      await deleteHelpArticle(selectedArticleId);
      toast.success("Article deleted");
      setSelectedArticle(null);
      setForm(emptyForm);
      await loadArticles();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to delete article");
    } finally {
      setDeleting(false);
    }
  };

  const filteredArticles = useMemo(() => {
    if (!searchTerm) return articles;
    const lower = searchTerm.toLowerCase();
    return articles.filter((article) =>
      [article.title, article.summary, article.slug]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(lower))
    );
  }, [articles, searchTerm]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Help Center</h2>
          <p className="text-sm text-gray-600">Manage published content and draft knowledge base articles.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setSearchTerm(searchValue.trim());
            }}
            className="flex items-center gap-2"
          >
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search articles"
              className="h-9 rounded-md border border-gray-200 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-gray-300"
            >
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setSearchValue("");
                }}
                className="text-sm text-gray-600 hover:underline"
              >
                Clear
              </button>
            )}
          </form>
        <a
          href="https://virtualscribe.ai/help-center"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300"
        >
          <ExternalLink className="h-4 w-4" /> View public site
        </a>

          <button
            type="button"
            onClick={handleStartNew}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-lg transition-all"
          >
            <Plus className="h-4 w-4" /> New article
          </button>
          <button
            type="button"
            onClick={loadArticles}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-1/2" />
              <col className="w-1/6" />
              <col className="w-1/6" />
              <col className="w-1/6" />
            </colgroup>
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredArticles.map((article) => {
                const isSelected = article.articleId === selectedArticleId;
                return (
                  <tr
                    key={article.articleId}
                    onClick={() => handleSelectArticle(article)}
                    className={`cursor-pointer hover:bg-blue-50 ${isSelected ? "bg-blue-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 line-clamp-1">{article.title || article.slug}</div>
                      <div className="text-xs text-gray-500 line-clamp-2">{article.summary || "No summary"}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{article.category || "Uncategorized"}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${article.status === "PUBLISHED" ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-700"}`}>
                        {article.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(article.updatedAt || article.publishedAt)}
                    </td>
                  </tr>
                );
              })}
              {(!filteredArticles || filteredArticles.length === 0) && !loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                    No articles found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div className="flex items-center justify-center px-4 py-6 text-sm text-gray-500">
              Loading help center articles...
            </div>
          )}
          {!loading && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
              <div>
                Page {pageInfo.page} of {pageInfo.pages} • {pageInfo.total} total
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => { setPage(1); setLimit(Number(e.target.value) || 50); }}
                  className="h-8 rounded-md border border-gray-200 px-2 text-xs"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pageInfo.page <= 1}
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageInfo.pages || 1, p + 1))}
                  disabled={pageInfo.page >= (pageInfo.pages || 1)}
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:sticky lg:top-4 self-start">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedArticleId ? "Edit article" : "Create article"}
              </h3>
              <p className="text-xs text-gray-500">
                {selectedArticleId ? `Status: ${selectedArticle?.status || '-'}` : "Start a new knowledge base entry."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsPreview((prev) => !prev)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-300"
              >
                {isPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {isPreview ? "Edit" : "Preview"}
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-blue-600 to-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:shadow-lg disabled:opacity-60 transition-all"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? "Saving..." : "Save draft"}
              </button>
              {selectedArticle?.status === "PUBLISHED" ? (
                <button
                  type="button"
                  onClick={handleUnpublish}
                  disabled={publishing}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  <CircleSlash className="h-3.5 w-3.5" />
                  {publishing ? "Working..." : "Unpublish"}
                </button>
              ) : selectedArticleId ? (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={publishing}
                  className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                  {publishing ? "Working..." : "Publish"}
                </button>
              ) : null}
              {selectedArticleId && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              )}
            </div>
          </div>

          {isPreview ? (
            <div className="max-w-none overflow-y-auto rounded-md border border-gray-200 p-4">
              {form.bodyMarkdown ? (
                <MarkdownRenderer content={form.bodyMarkdown} variant="light" />
              ) : (
                <p className="text-sm text-gray-500">Start typing content to see a preview.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(event) => handleFormChange("title", event.target.value)}
                    placeholder="How to invite clinicians"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Slug</label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(event) => handleFormChange("slug", slugify(event.target.value))}
                    placeholder="invite-clinicians"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Summary</label>
                <textarea
                  value={form.summary}
                  onChange={(event) => handleFormChange("summary", event.target.value)}
                  rows={2}
                  placeholder="Short description for list views"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Category</label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(event) => handleFormChange("category", event.target.value)}
                    placeholder="Getting started"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(event) => handleFormChange("tags", event.target.value)}
                    placeholder="setup, onboarding"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Content (Markdown)</label>
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Quick insert:</span>
                  <button type="button" onClick={quickInsert.h1} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Heading1 className="h-3.5 w-3.5" /> H1</button>
                  <button type="button" onClick={quickInsert.h2} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Heading2 className="h-3.5 w-3.5" /> H2</button>
                  <button type="button" onClick={quickInsert.h3} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Heading3 className="h-3.5 w-3.5" /> H3</button>
                  <button type="button" onClick={quickInsert.p} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300">Paragraph</button>
                  <button type="button" onClick={quickInsert.bold} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Bold className="h-3.5 w-3.5" /> Bold</button>
                  <button type="button" onClick={quickInsert.italic} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Italic className="h-3.5 w-3.5" /> Italic</button>
                  <button type="button" onClick={quickInsert.link} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><LinkIcon className="h-3.5 w-3.5" /> Link</button>
                  <button type="button" onClick={quickInsert.ul} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><List className="h-3.5 w-3.5" /> Bullets</button>
                  <button type="button" onClick={quickInsert.ol} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><ListOrdered className="h-3.5 w-3.5" /> Numbered</button>
                  <button type="button" onClick={quickInsert.quote} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Quote className="h-3.5 w-3.5" /> Quote</button>
                  <button type="button" onClick={quickInsert.code} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Code className="h-3.5 w-3.5" /> Code</button>
                  <button type="button" onClick={quickInsert.table} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><Table className="h-3.5 w-3.5" /> Table</button>
                  <button type="button" onClick={quickInsert.image} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><ImageIcon className="h-3.5 w-3.5" /> Image</button>
                  <button type="button" onClick={quickInsert.template} className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300"><FilePlus className="h-3.5 w-3.5" /> Template</button>
                  <span className="ml-auto text-xs text-gray-500">Headings and paragraphs render on the article page.</span>
                </div>
                <textarea
                  ref={contentRef}
                  value={form.bodyMarkdown}
                  onChange={(event) => handleFormChange("bodyMarkdown", event.target.value)}
                  rows={12}
                  placeholder="## Heading\nDetail the workflow..."
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
          )}

          {selectedArticle && (
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              <div>Last updated: {formatDate(selectedArticle.updatedAt)}</div>
              {selectedArticle.publishedAt && <div>Published: {formatDate(selectedArticle.publishedAt)}</div>}
              <div className="flex items-center gap-1">
                <span>URL:</span>
                <a
                  href={`https://virtualscribe.ai/help-center/${selectedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  https://virtualscribe.ai/help-center/{selectedArticle.slug}
                  <ExternalLink className="h-3 w-3 inline" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenterAdmin;
